// src/controllers/useOrdersPageController.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL, BASE_IMAGE_URL } from "../utils/api";

/* === Local helpers (same behavior as your page) ========================== */
const PLACEHOLDER = (seed) =>
  `https://picsum.photos/seed/${encodeURIComponent(seed)}/180/120`;

function mapStatusToTab(status) {
  const s = String(status || "").toLowerCase();
  if (["delivered"].includes(s)) return "completed";
  if (["rejected", "cancelled", "canceled"].includes(s)) return "cancelled";
  return "inprogress";
}

function fmtDate(dtStr) {
  if (!dtStr) return "—";

  // Normalize strings like "2025-09-30T21:14:10.000000Z" → millisecond precision
  try {
    const normalized = String(dtStr)
      .replace(' ', 'T')
      .replace(/(\.\d{3})\d+Z$/, '$1Z');

    const d = new Date(normalized);
    if (isNaN(d)) return dtStr; // fallback: show raw if parsing failed

    // Format in Arabic, 24-hour, UTC (change timeZone if you want local)
    return d.toLocaleString('ar-EG', {
      timeZone: 'UTC',      // or 'Asia/Damascus' for local Syrian time
      hourCycle: 'h23',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {

  }

}


/** turn a relative image path into an absolute URL */
const toAbsImg = (u) => {
  if (!u) return "";
  return /^https?:\/\//i.test(u) ? u : `${BASE_IMAGE_URL}${u}`;
};
/** first image of a part (fallback to a placeholder) */
const partThumb = (part, fallbackSeed = "thumb") => {
  const imgs = part?.images;
  const raw =
    Array.isArray(imgs) && imgs.length
      ? (imgs[0]?.image_url || imgs[0]?.url || "")
      : "";
  return raw
    ? toAbsImg(raw)
    : `https://picsum.photos/seed/${encodeURIComponent(fallbackSeed)}/180/120`;
};

function normalizeCarts(carts = []) {

  const rows = carts.map((c) => {

    // الكميات
    let qty = Number(c.total_quantity || 0);
    if (!qty) {
      qty = Array.isArray(c.items)
        ? c.items.reduce((s, it) => s + (Number(it.quantity || 0) || 1), 0)
        : 0;
    }

    // صور مصغّرة
    const images = (c.items || [])
      .slice(0, 2)
      .map((it, i) => partThumb(it.part, `cart-${c.id}-item-${i}`));

    // الحقول المعيارية
    const status = String(c.status || "").trim().toLowerCase();
    const worker = String(c.worker_status || "").trim().toLowerCase();

    // ⛔ لا نعرض الطلبات بحالة pending إطلاقاً
    if (status === "pending") return null;

    // التبويب + تواريخ العرض
    let tab = "inprogress";
    let processingAt = c.processing_at || null;
    let deliveredAt = c.delivered_at || null;
    let cancelledAt = c.rejected_at || null;
    let updatedAt = c.updated_at || null;

    // القواعد المطلوبة
    if (status === "done" && worker != "delivered" && worker != 'rejected') {
      tab = "inprogress";
      processingAt = c.processing_at || c.approved_at || c.created_at || null;
    } else if (status === "done" && worker === "delivered") {
      tab = "completed";
      deliveredAt = c.delivered_at || null;
    } else if (
      status === "cancelled" || status === "canceled" ||
      status === "refund" || status === "refunded"
    ) {
      tab = "cancelled";
      cancelledAt = c.updated_at || c.rejected_at || null; // حسب طلبك
    } else {
      // Fallbacks غير متعلقة بـ pending
      if (status === "delivered" || worker === "delivered") {
        tab = "completed";
        deliveredAt = c.delivered_at || c.updated_at || null;
      } else if (status === "rejected") {
        tab = "cancelled";
        cancelledAt = c.updated_at || c.rejected_at || null;
      } else {
        // أي حالة غير معروفة ولا تنطبق على القواعد → لا نعرضها
        return null;
      }
    }

    const etaText =
      tab === "inprogress" && processingAt ? fmtDate(processingAt) : "";

    return {
      id: c.id,
      qty,
      totalPrice: Number(c.total_price || 0),
      tab,
      createdAt: c.created_at,
      updatedAt,
      processingAt,
      deliveredAt,
      cancelledAt,
      etaText,
      images,
      raw: c,
      city: c.city,
      items: c.items,
      worker,
      status
    };
  });

  // ✅ احذف العناصر null (مثل pending أو أي حالة استبعدناها)
  return rows.filter(Boolean);
}



// Try to pull EdfaPay order_id from cart shapes
function extractOrderIdFromCart(cart) {
  try {
    if (!cart || typeof cart !== "object") return null;
    if (cart.order_id) return cart.order_id;
    if (cart.payment?.order_id) return cart.payment.order_id;
    if (cart.last_payment?.order_id) return cart.last_payment.order_id;
    if (Array.isArray(cart.payments) && cart.payments.length) {
      const sorted = [...cart.payments].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      return sorted[0]?.order_id || cart.payments[0]?.order_id || null;
    }
  } catch {

    return null;
  }

}

/* === Controller ========================================================== */
export default function useOrdersPageController() {
  const token = localStorage.getItem("token");

  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("inprogress");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [activeOrder, setActiveOrder] = useState(null);

  const [guestPhone, setGuestPhone] = useState(localStorage.getItem("guestPhone") || "");
  const [guestLoaded, setGuestLoaded] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState("");

  const canView = Boolean(token) || guestLoaded;

  // Authenticated fetch
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axios.get(`${API_BASE_URL}/carts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const data = Array.isArray(res.data) ? res.data : [];
          setOrders(normalizeCarts(data));
        }
      } catch (e) {
        console.error("Orders fetch failed:", e);
        if (!cancelled) setErr("تعذّر تحميل الطلبات.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Guest lookup by phone (same handler signature)
  const fetchByPhone = async (e) => {
    e.preventDefault();
    const phone = guestPhone.replace(/[^\d]/g, "");
    if (!phone || phone.length < 8) {
      setGuestError("أدخل رقم جوال صحيح.");
      return;
    }

    try {
      setGuestError("");
      setGuestLoading(true);
      localStorage.setItem("guestPhone", phone);

      const res = await axios.post(
        `${API_BASE_URL}/get-carts/by-phone`,
        { phone },
        { headers: { "Content-Type": "application/json" } }
      );

      // Handle backend “no carts” condition even if HTTP is 200
      const status = res?.data?.status;
      const message = res?.data?.message;
      const carts = Array.isArray(res?.data?.carts) ? res.data.carts : [];

      if (status === false && /no carts/i.test(String(message || ""))) {
        // Saudi dialect message as requested
        setOrders([]);
        setGuestLoaded(true);
        setGuestError("ما عندك طلبيات على هذا الرقم.");
        return;
      }

      setOrders(normalizeCarts(carts));
      setGuestLoaded(true);

      if (carts.length === 0) {
        // fallback when API returns empty array without status=false
        setGuestError("ما عندك طلبيات على هذا الرقم.");
      }
    } catch (err) {
      console.error("Guest orders fetch failed:", err);

      // If server provided a “no carts” message on error status codes
      const apiMsg = err?.response?.data?.message || "";
      if (/no carts/i.test(apiMsg)) {
        setGuestError("ما عندك طلبيات على هذا الرقم.");
      } else {
        setGuestError("تعذّر جلب الطلبات. حاول مرة ثانية.");
      }
    } finally {
      setGuestLoading(false);
    }
  };


  const filtered = useMemo(() => orders.filter((o) => o.tab === tab), [orders, tab]);

  const openOrderDetails = (order) => {
    
    const c = order.raw;
    const worker = String(c.worker_status || "").trim().toLowerCase();
    const status = String(c.status || "").trim().toLowerCase();

    const items = (c.items || []).map((it, idx) => {
      const p = it.part || {};
      const qty = Number(it.quantity || 0) || 1;
      const price = Number(it.price || p.price || 0);
      const delivery = Number(p.delivery_price || 0);
      return {
        id: it.id || `li-${idx}`,
        name: p.name || `قطعة #${p.id || "?"}`,
        image: partThumb(p, `cart-${c.id}-part-${p.id || idx}`),
        price,
        qty,
        delivery,
        part_id: it.part_id
      };
    });

    const deliveryFee = items.reduce((s, it) => s + it.delivery * it.qty, 0);
    const subtotal = items.reduce((s, it) => s + it.price * it.qty, 0);
    const grandTotal = Number(c.total_price || 0) || subtotal + deliveryFee;
    const tab = order.tab;

    setActiveOrder({
      cartId: c.id,
      orderId: extractOrderIdFromCart(c), // needed for cancel
      items,
      deliveryFee,
      subtotal,
      grandTotal,
      tab,
      worker,
      status,
      recipientName: c.full_name || "—",
      recipientPhone: c.phone_number || "—",
      arrivalAt: c.rejected_at
        ? fmtDate(c.rejected_at)
        : c.delivered_at
          ? fmtDate(c.delivered_at)
          : c.created_at
            ? fmtDate(c.created_at)
            : "—",
    });
  };

  const closeOrderDetails = () => setActiveOrder(null);

  // Called by modal after cancel success
  const markOrderCancelled = (cartId) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === cartId
          ? {
            ...o,
            tab: "cancelled",
            cancelledAt: new Date().toISOString(),
            raw: { ...o.raw, status: "cancelled", rejected_at: new Date().toISOString() },
          }
          : o
      )
    );
  };


  return {
    // state for view
    tab,
    setTab,
    orders,
    loading,
    err,
    filtered,
    activeOrder,
    canView,

    // guest gate
    guestPhone,
    setGuestPhone,
    guestLoaded,
    guestLoading,
    guestError,
    fetchByPhone,

    // details
    openOrderDetails,
    closeOrderDetails,
    markOrderCancelled,
  };
}
